import { useEffect, useState, useMemo } from 'react';
import { adminApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Select from '../../components/ui/Select.jsx';
import Badge from '../../components/ui/Badge.jsx';
import Modal from '../../components/ui/Modal.jsx';
import { Card, CardContent } from '../../components/ui/Card.jsx';
import SendReminderButton from '../../components/admin/SendReminderButton.jsx';
import UserMultiSelect from '../../components/admin/UserMultiSelect.jsx';
import { cn, formatDays } from '@/lib/utils';
import { Upload, Trash2, AlertTriangle, Loader2, FileText, Mail } from 'lucide-react';

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'no_notifiees', label: 'No Notifiees' },
  { value: 'expiring', label: 'Expiring Soon' },
  { value: 'expired', label: 'Expired' },
  { value: 'none', label: 'No Contract' },
  { value: 'unconfigured', label: 'Needs Leave Config' },
];

// contractNotifyUserIds may arrive as an array or JSON string —
// count people configured to receive the expiry reminder.
function notifieeCount(value) {
  if (Array.isArray(value)) return value.filter(Boolean).length;
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.filter(Boolean).length : 0;
    } catch {
      return 0;
    }
  }
  return 0;
}

export default function ContractsPage() {
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [teamFilter, setTeamFilter] = useState('all');
  const [reminderDays, setReminderDays] = useState(30);
  const [editingUser, setEditingUser] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [usersRes, teamsRes, expiringRes, typesRes] = await Promise.all([
        adminApi.listUsers(),
        adminApi.listTeams().catch(() => ({ teams: [] })),
        adminApi.getExpiringContracts().catch(() => ({ reminderDays: 30 })),
        adminApi.listLeaveTypes().catch(() => ({ types: [] })),
      ]);
      setUsers((usersRes.users || []).filter((u) => u.isActive !== false));
      setTeams(teamsRes.teams || []);
      setReminderDays(expiringRes.reminderDays || 30);
      setLeaveTypes((typesRes.types || []).filter((t) => t.isActive !== false));
    } catch (err) {
      toast.error(`Failed to load: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const activeTeams = teams.filter((t) => t.isActive !== false);

  function getContractStatus(user) {
    if (!user.contractBlobPath) return 'none';
    if (!user.contractExpiryDate) return 'active';
    const today = new Date().toISOString().substring(0, 10);
    if (user.contractExpiryDate < today) return 'expired';
    const userDays = user.contractReminderDays ?? reminderDays;
    const cutoff = new Date(Date.now() + userDays * 86400000).toISOString().substring(0, 10);
    if (user.contractExpiryDate <= cutoff) return 'expiring';
    // Active and not yet near expiry, but no one's set to receive the
    // reminder — flag it yellow so the admin notices in advance.
    if (notifieeCount(user.contractNotifyUserIds) === 0) return 'no_notifiees';
    return 'active';
  }

  function getDaysUntil(dateStr) {
    if (!dateStr) return null;
    return Math.ceil((new Date(dateStr) - new Date()) / 86400000);
  }

  const filtered = useMemo(() => {
    let list = users;
    if (filter === 'unconfigured') {
      list = list.filter((u) => !u.annualLeaveEntitlement);
    } else if (filter !== 'all') {
      list = list.filter((u) => getContractStatus(u) === filter);
    }
    if (teamFilter !== 'all') {
      if (teamFilter === 'unassigned') {
        list = list.filter((u) => !u.teamId);
      } else {
        list = list.filter((u) => u.teamId === teamFilter);
      }
    }
    if (search) {
      const s = search.toLowerCase();
      list = list.filter((u) =>
        u.displayName?.toLowerCase().includes(s) ||
        u.email?.toLowerCase().includes(s)
      );
    }
    return list.sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
  }, [users, search, filter, teamFilter, reminderDays]); // eslint-disable-line react-hooks/exhaustive-deps

  const expiringCount = users.filter((u) => getContractStatus(u) === 'expiring').length;
  const expiredCount = users.filter((u) => getContractStatus(u) === 'expired').length;
  const unconfiguredCount = users.filter((u) => !u.annualLeaveEntitlement).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-6 w-6 animate-spin rounded-full border-[2.5px] border-border border-t-primary" />
      </div>
    );
  }

  // A helper — avoid propagating clicks from inline quick-edit controls to the
  // row click handler (which would otherwise open the full edit modal).

  return (
    <div>
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Employee Contracts & Leave Config</h1>
          <p className="text-muted-foreground">
            {users.filter((u) => u.contractBlobPath).length} contracts uploaded
            {unconfiguredCount > 0 && <span className="text-amber-500"> · {unconfiguredCount} need leave config</span>}
            {expiringCount > 0 && <span className="text-warning"> · {expiringCount} expiring soon</span>}
            {expiredCount > 0 && <span className="text-destructive"> · {expiredCount} expired</span>}
          </p>
        </div>
        <SendReminderButton kinds={['contracts']} label="Send contract reminders" />
      </div>

      {(expiringCount > 0 || expiredCount > 0 || unconfiguredCount > 0) && (
        <Card className="mb-6 border-warning/30 bg-warning/5">
          <CardContent className="flex items-start gap-3 py-4">
            <AlertTriangle className="mt-0.5 h-4 w-4 text-warning shrink-0" />
            <div className="text-sm">
              {unconfiguredCount > 0 && <p className="text-amber-500 font-medium">{unconfiguredCount} employee{unconfiguredCount !== 1 ? 's' : ''} missing leave entitlement configuration</p>}
              {expiredCount > 0 && <p className="text-destructive font-medium">{expiredCount} contract{expiredCount !== 1 ? 's' : ''} expired</p>}
              {expiringCount > 0 && <p className="text-warning">{expiringCount} contract{expiringCount !== 1 ? 's' : ''} expiring within {reminderDays} days</p>}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex gap-3 mb-5 items-end flex-wrap">
        <Input label="Search" placeholder="Search by name or email..." value={search} onChange={(e) => setSearch(e.target.value)} className="flex-1 max-w-[400px]" />
        <Select label="Team" value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)}
          options={[{ value: 'all', label: 'All teams' }, { value: 'unassigned', label: 'Unassigned' }, ...activeTeams.map((t) => ({ value: t.rowKey, label: t.displayName }))]} />
        <div className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((f) => (
            <button key={f.value} onClick={() => setFilter(f.value)}
              className={cn('rounded-md border px-3 py-1.5 text-sm transition-colors cursor-pointer',
                filter === f.value ? 'border-primary bg-primary/10 font-semibold text-primary' : 'border-input text-muted-foreground hover:bg-muted')}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Employee</th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Contract</th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Expiry</th>
                <th className="px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">AL Entitlement</th>
                <th className="px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Monthly</th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const status = getContractStatus(u);
                const monthlyAccrual = u.annualLeaveEntitlement ? (u.annualLeaveEntitlement / 12) : null;
                const days = getDaysUntil(u.contractExpiryDate);
                return (
                  <tr
                    key={u.rowKey}
                    onClick={() => setEditingUser(u)}
                    className="border-b border-border/50 cursor-pointer hover:bg-accent/50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium">{u.displayName}</div>
                      <div className="text-xs text-muted-foreground">{u.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      {u.contractFileName ? (
                        <span className="text-xs font-mono truncate max-w-[150px] block">{u.contractFileName}</span>
                      ) : (
                        <span className="text-muted-foreground/40">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {u.contractExpiryDate ? (
                        <span className="text-xs font-mono">{u.contractExpiryDate}</span>
                      ) : (
                        <span className="text-muted-foreground/40">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {u.annualLeaveEntitlement ? (
                        <span className="font-mono font-semibold text-primary">{formatDays(u.annualLeaveEntitlement)}d</span>
                      ) : (
                        <Badge variant="warning">Not set</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {monthlyAccrual ? (
                        <span className="font-mono text-xs text-muted-foreground">{monthlyAccrual.toFixed(2)}d/mo</span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {status === 'active' && <Badge variant="success">Active</Badge>}
                      {status === 'no_notifiees' && <Badge variant="warning">No notifiees</Badge>}
                      {status === 'expiring' && <Badge variant="warning">{days}d left</Badge>}
                      {status === 'expired' && <Badge variant="danger">Expired</Badge>}
                      {status === 'none' && <Badge variant="default">None</Badge>}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-muted-foreground">No employees match your filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editingUser && (
        <ContractEditModal
          user={editingUser}
          leaveTypes={leaveTypes}
          allUsers={users}
          onClose={() => setEditingUser(null)}
          onUserUpdated={(patch) => setUsers((prev) => prev.map((x) => x.rowKey === editingUser.rowKey ? { ...x, ...patch } : x))}
        />
      )}
    </div>
  );
}

function ContractEditModal({ user, leaveTypes, allUsers = [], onClose, onUserUpdated }) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [entitlement, setEntitlement] = useState(user.annualLeaveEntitlement || '');
  const [accrualStart, setAccrualStart] = useState(user.accrualStartDate || user.hireDate || '');
  const [employeeId, setEmployeeId] = useState(user.employeeId || '');
  const [officeLocation, setOfficeLocation] = useState(user.officeLocation || '');

  // Per-contract notifiees. Stored as JSON-encoded string[] on the
  // user row (contractNotifyUserIds). expiryNotifier fans out one
  // personalized email + in-app pop-up per id when expiry is near.
  const [notifyUserIds, setNotifyUserIds] = useState(() => {
    if (Array.isArray(user.contractNotifyUserIds)) return user.contractNotifyUserIds;
    if (typeof user.contractNotifyUserIds === 'string' && user.contractNotifyUserIds) {
      try { return JSON.parse(user.contractNotifyUserIds); } catch { return []; }
    }
    return [];
  });
  const [prevUsed, setPrevUsed] = useState(() => {
    try { return user.previousUsed ? JSON.parse(user.previousUsed) : {}; } catch { return {}; }
  });
  const [prevCarried, setPrevCarried] = useState(() => {
    try { return user.previousCarriedOver ? JSON.parse(user.previousCarriedOver) : {}; } catch { return {}; }
  });

  // Contract document state — managed inline in the modal so admins do
  // everything from one place. Local mirrors of the user's current
  // contract fields keep the modal UI responsive between actions.
  const [contractFileName, setContractFileName] = useState(user.contractFileName || '');
  const [contractBlobPath, setContractBlobPath] = useState(user.contractBlobPath || '');
  const [contractExpiryDate, setContractExpiryDate] = useState(user.contractExpiryDate || '');
  const [uploading, setUploading] = useState(false);
  const [deletingContract, setDeletingContract] = useState(false);
  const [uploadingFileName, setUploadingFileName] = useState('');

  async function handleUploadContract(file) {
    if (!file) return;
    setUploading(true);
    setUploadingFileName(file.name);
    try {
      const formData = new FormData();
      formData.append('contract', file);
      if (contractExpiryDate) formData.append('expiryDate', contractExpiryDate);
      const res = await adminApi.uploadContract(user.rowKey, formData);
      const newName = res?.fileName || file.name;
      const newPath = res?.blobPath || `${user.rowKey}/contract`;
      setContractFileName(newName);
      setContractBlobPath(newPath);
      onUserUpdated({ contractFileName: newName, contractBlobPath: newPath, contractExpiryDate });
      toast.success('Contract uploaded');
    } catch (err) {
      toast.error(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
      setUploadingFileName('');
    }
  }

  async function handleRemoveContract() {
    if (!confirm(`Remove contract for ${user.displayName}?`)) return;
    setDeletingContract(true);
    try {
      await adminApi.deleteContract(user.rowKey);
      setContractFileName('');
      setContractBlobPath('');
      onUserUpdated({ contractFileName: '', contractBlobPath: '' });
      toast.success('Contract removed');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDeletingContract(false);
    }
  }

  async function handleExpiryChange(date) {
    setContractExpiryDate(date);
    try {
      await adminApi.updateUser(user.rowKey, { contractExpiryDate: date });
      onUserUpdated({ contractExpiryDate: date });
    } catch (err) {
      toast.error(err.message);
    }
  }

  async function handleSaveConfig() {
    setSaving(true);
    try {
      const data = {
        annualLeaveEntitlement: parseFloat(entitlement) || 0,
        accrualStartDate: accrualStart,
        employeeId,
        officeLocation,
        previousUsed: JSON.stringify(prevUsed),
        previousCarriedOver: JSON.stringify(prevCarried),
        contractNotifyUserIds: JSON.stringify(notifyUserIds || []),
      };
      await adminApi.updateUser(user.rowKey, data);
      onUserUpdated(data);
      toast.success(`Saved leave config for ${user.displayName}`);
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={`Leave Configuration — ${user.displayName}`}
      width="780px"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSaveConfig} loading={saving} disabled={saving}>Save</Button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="grid grid-cols-4 gap-4">
          <Input label="Employee ID" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} placeholder="e.g. EMP001" />
          <Input label="Office Location" value={officeLocation} onChange={(e) => setOfficeLocation(e.target.value)} placeholder="e.g. Riyadh" />
          <Input label="Annual Leave Entitlement (days/year)" type="number" min="0" step="0.01" value={entitlement} onChange={(e) => setEntitlement(e.target.value)} placeholder="e.g. 21" />
          <div>
            <label className="block text-[13px] font-medium text-muted-foreground mb-1.5">Monthly Accrual</label>
            <div className="h-10 flex items-center px-3 rounded-md border border-border bg-muted/30 text-sm font-mono">
              {entitlement ? (parseFloat(entitlement) / 12).toFixed(2) : '—'} days/mo
            </div>
          </div>
        </div>

        <Input label="Accrual Start Date" type="date" value={accrualStart} onChange={(e) => setAccrualStart(e.target.value)} className="max-w-[200px]" />

        {/* Contract Document */}
        <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Contract Document</div>
            {uploading && (
              <div className="flex items-center gap-1.5 text-xs text-primary">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                <span>Uploading{uploadingFileName ? ` ${uploadingFileName}` : ''}…</span>
              </div>
            )}
          </div>

          {contractBlobPath ? (
            <div className="flex items-center gap-3 rounded-md border border-border bg-card px-3 py-2.5">
              <FileText className="h-4 w-4 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{contractFileName || 'Contract on file'}</div>
                <div className="text-xs text-muted-foreground">Uploaded</div>
              </div>
              <button
                onClick={handleRemoveContract}
                disabled={deletingContract || uploading}
                className="rounded-md p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
                title="Remove contract"
              >
                {deletingContract ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              </button>
            </div>
          ) : (
            <div className="text-xs text-muted-foreground italic">No contract uploaded yet.</div>
          )}

          <div className="flex items-end gap-3 flex-wrap">
            <div className="w-[200px]">
              <label className="block text-[13px] font-medium text-muted-foreground mb-1.5">Expiry Date</label>
              <input
                type="date"
                value={contractExpiryDate}
                onChange={(e) => handleExpiryChange(e.target.value)}
                disabled={uploading}
                className="w-full bg-card border border-border rounded-md px-3 py-2 text-sm focus:border-primary/40 focus:outline-none transition-colors disabled:opacity-50"
              />
            </div>
            <label className={`inline-flex items-center gap-2 rounded-md border border-dashed border-border px-4 py-2 text-sm font-medium transition-colors ${uploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-primary/40 hover:bg-primary/5 hover:text-foreground'}`}>
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              <span>{uploading ? 'Uploading…' : (contractBlobPath ? 'Replace contract' : 'Upload contract')}</span>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                className="hidden"
                disabled={uploading}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUploadContract(f); e.target.value = ''; }}
              />
            </label>
          </div>
        </div>

        {/* Contract expiry notifiees */}
        <div className="rounded-lg border border-border bg-muted/30 p-4">
          <UserMultiSelect
            label="Notify these people about contract expiry"
            users={allUsers}
            value={notifyUserIds}
            onChange={setNotifyUserIds}
            placeholder="Pick one or more notifiees..."
            helpText="Each selected person gets a personal email and an in-app pop-up when this contract is in the reminder window or has expired."
          />
        </div>

        {/* Previous Used */}
        <div>
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Previous Days Used (before KSA ProHR)</div>
          <div className="grid grid-cols-4 gap-2">
            {leaveTypes.map((t) => (
              <div key={t.rowKey} className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground w-20 shrink-0 truncate" title={t.displayName}>{t.abbreviation || t.rowKey}</span>
                <input type="number" min="0" step="0.01"
                  value={prevUsed[t.rowKey] || ''}
                  onChange={(e) => setPrevUsed((p) => ({ ...p, [t.rowKey]: parseFloat(e.target.value) || 0 }))}
                  placeholder="0"
                  className="w-16 bg-card border border-border rounded-md px-2 py-1 text-xs font-mono text-center focus:border-primary/40 focus:outline-none transition-colors"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Previous Carried Over */}
        <div>
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Previous Carry-Over (from last year)</div>
          <div className="grid grid-cols-4 gap-2">
            {leaveTypes.map((t) => (
              <div key={t.rowKey} className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground w-20 shrink-0 truncate" title={t.displayName}>{t.abbreviation || t.rowKey}</span>
                <input type="number" min="0" step="0.01"
                  value={prevCarried[t.rowKey] || ''}
                  onChange={(e) => setPrevCarried((p) => ({ ...p, [t.rowKey]: parseFloat(e.target.value) || 0 }))}
                  placeholder="0"
                  className="w-16 bg-card border border-border rounded-md px-2 py-1 text-xs font-mono text-center focus:border-primary/40 focus:outline-none transition-colors"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}
